import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { Accordion, Card, OverlayTrigger, Tooltip, Popover } from "react-bootstrap";
import { useSortableData, REProjectHeaders as headers, renderStatus, renderActive } from "../dashboard-util/dashboard-utils";
import ArrowDown from "../../../assets/icons/ArrowDown.svg";
import ArrowUp from "../../../assets/icons/ArrowUp.svg";
import ArrowHover from "../../../assets/icons/ArrowHover.svg";
import { DashboardExport } from "../../../assets/icons";
import ViewMembersPanelNew from "../../../components/view-members-new/view-members-new";
import ProjectTimeLogModal from "./ProjectTimeLogModal";
import { GetREProjectLogExcelExport } from "../../../services/api/dashboard-api";
import DormantAccordion from "./DormantAccordian";

import "../dashboard-cme/dashboard-cme.scss";
import "./dashboard-real-estate.scss";

const DashboardProjectTimeLogTracker = ({ realEstateProjectLog, isStaffer }) => {
    const [projects, setProjects] = useState(realEstateProjectLog);
    const [show, setShow] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const { items: ActiveItems, requestSort: requestActiveSort, getArrowDirectionFor: getArrowDirectionForActive } = useSortableData(projects?.ActiveProjectLog);
    const { items: InactiveItems, requestSort: requestInactiveSort, getArrowDirectionFor: getArrowDirectionForInactive } = useSortableData(projects?.InactiveProjectLog);

    useEffect(() => {
        setProjects(realEstateProjectLog);
    }, [realEstateProjectLog]);

    const toggleModal = (project = null) => {
        setShow(!!project);
        setSelectedProject(project);
    };

    if (!isStaffer) return null;

    const renderCell = (sortKey, colClass, project, isActive) => {
        const cellData = project[sortKey];
        const renderContent = () => {
            switch (sortKey) {
                case "ProjectName":
                    return <span className={isActive ? "project-name" : ""}>{cellData}</span>;
                case "ProjectMembersList":
                    return <ViewMembersPanelNew UserID={project.OwnerUserId} members={project.ProjectMembersList} projects={project} isHidden />;
                case "Status":
                    return renderStatus(project.Status, project.StatusColor);
                case "Active":
                    return renderActive(project.Active);
                case "Description":
                    const truncated = cellData?.length > 40 ? `${cellData.substring(0, 40)}...` : cellData;
                    return (
                        <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={<Popover id={`popover-${sortKey}`}><Popover.Content>{cellData}</Popover.Content></Popover>}>
                            <span>{truncated}</span>
                        </OverlayTrigger>
                    );
                default:
                    return cellData !== undefined ? cellData : "-";
            }
        };
        return (
            <td key={sortKey} className={`mt-2 ${colClass}`} onClick={() => sortKey === "ProjectName" && isActive && toggleModal(project)}>
                {renderContent()}
            </td>
        );
    };

    const renderProjectTable = (items, requestSort, getArrowDirectionFor, isActive) => (
        <div className="dashboard-table-scrollable">
            <table className="CMEDashboardTable">
                <thead className="height-45">
                    <tr className="height-45">
                        {headers.map(({ text, sortKey, colClass }) => (
                            <th className={`${colClass} headerSorted widgetHeader REwidgetHeader`} key={text}>
                                    <button 
                                        onClick={() => requestSort(sortKey)} 
                                        onKeyDown={(e) => e.key === 'Enter' && requestSort(sortKey)}
                                        className="sortedLeft" 
                                        aria-label="Sort Column"
                                    >
                                    <div className="containerTextSortedLeft minWidth-150">
                                        <p className="wrapText">{text}</p>
                                    </div>
                                    {getArrowDirectionFor?.(sortKey, "descending") && <img className="arrowDirection" alt="Arrow down" src={ArrowDown} />}
                                    {getArrowDirectionFor?.(sortKey, "ascending") && <img className="arrowDirection" alt="Arrow up" src={ArrowUp} />}
                                    {getArrowDirectionFor?.(sortKey) && <img className="arrowDirectionHover" alt="Arrow sort" src={ArrowHover} />}
                                </button>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="tableBody cmeBody">
                    {items?.map((project) => (
                        <tr key={project?.ProjectId} className={isActive ? "height-47" : "dormantRow height-47"}>
                            {headers.map(({ sortKey, colClass }) => renderCell(sortKey, colClass, project, isActive))}                            
                        </tr>
                    ))}
                </tbody>
            </table>
            {selectedProject && isActive && <ProjectTimeLogModal project={selectedProject} show={show} onHide={() => toggleModal()} />}
        </div>
    );

    return (
        <div className="new-dashboard table-wrapper marginTopWidget">
            <div className="DashboardTableWrapper CMEDashboard">
                <div className="DashboardTableHeader">
                    <th className="DashboardTableTitle">
                        Project Time Log
                        <div className="DashboardTableHeaderButtons">
                            <OverlayTrigger placement="top" overlay={<Tooltip>Export Project Log Data</Tooltip>}>
                            <input 
                                type="image"
                                src={DashboardExport} 
                                alt="Export RE" 
                                onClick={GetREProjectLogExcelExport} 
                                onKeyDown={(e) => e.key === 'Enter' && GetREProjectLogExcelExport()} 
                                aria-label="Export Real Estate Project Log"
                            />
                            </OverlayTrigger>
                        </div>
                        <div className="subDepartmentTitle">GIB/Real Estate</div>
                    </th>
                </div>
                {projects?.ActiveProjectLog.length > 0 && renderProjectTable(ActiveItems, requestActiveSort, getArrowDirectionForActive, true)}
                <Accordion>
                    <div className="dormantContainer">
                        <DormantAccordion eventKey="0" projectCount={projects?.InactiveProjectLog?.length ?? 0} />
                        <Accordion.Collapse eventKey="0">
                            <Card.Body>
                                {projects?.InactiveProjectLog.length > 0 && renderProjectTable(InactiveItems, requestInactiveSort, getArrowDirectionForInactive, false)}
                            </Card.Body>
                        </Accordion.Collapse>
                    </div>
                </Accordion>
            </div>
        </div>
    );
};

DashboardProjectTimeLogTracker.propTypes = {
    realEstateProjectLog: PropTypes.shape({
        ActiveProjectLog: PropTypes.arrayOf(
            PropTypes.shape({
                ProjectId: PropTypes.number.isRequired,
                ProjectName: PropTypes.string.isRequired,
                OwnerUserId: PropTypes.number.isRequired,
                OwnerUserName: PropTypes.string,
                ProjectMembersList: PropTypes.arrayOf(PropTypes.shape({
                    UserId: PropTypes.number.isRequired,
                    UserName: PropTypes.string,
                    UserNameFull: PropTypes.string,
                    Initials: PropTypes.string
                })),
                Status: PropTypes.string,
                StatusColor: PropTypes.string,
                HoursWorkedInCurrentWeek: PropTypes.number,
                CountWorkingOnTheWeekend: PropTypes.number,
                HoursForTheNextWeek: PropTypes.number,
                MD: PropTypes.string,
                D: PropTypes.string,
                VP: PropTypes.string,
                Associate: PropTypes.string,
                Analyst: PropTypes.string,
                Description: PropTypes.string,
                LastUpdated: PropTypes.string,
                Active: PropTypes.number.isRequired
            })
        ),
        InactiveProjectLog: PropTypes.array,
    }).isRequired,
    isStaffer: PropTypes.bool.isRequired,
};

export default React.memo(DashboardProjectTimeLogTracker);
